import { Button, Group, Select, Tooltip } from "@mantine/core";
import { useMediaQuery } from "@mantine/hooks";
import { MOBILE_MEDIA_QUERY } from "@/components/ui";
import styles from "./AlphabetFilter.module.css";

const LETTERS = [
  "#",
  "A",
  "B",
  "C",
  "D",
  "E",
  "F",
  "G",
  "H",
  "I",
  "J",
  "K",
  "L",
  "M",
  "N",
  "O",
  "P",
  "Q",
  "R",
  "S",
  "T",
  "U",
  "V",
  "W",
  "X",
  "Y",
  "Z",
] as const;

export type AlphabetLetter = (typeof LETTERS)[number] | "ALL";

/** Map of first character to count */
export type AlphabetCounts = Map<string, number>;

interface AlphabetFilterProps {
  /** Currently selected letter (null means "ALL") */
  selected: AlphabetLetter | null;
  /** Callback when a letter is selected */
  onSelect: (letter: AlphabetLetter | null) => void;
  /** Optional counts per letter from alphabetical-groups endpoint */
  counts?: AlphabetCounts;
  /** Total count (for ALL button) */
  totalCount?: number;
}

/**
 * Alphabetical A-Z filter bar for filtering series by the first letter of their sort title.
 *
 * - "ALL" shows all series (no filter)
 * - "#" shows series starting with a number or special character
 * - A-Z shows series starting with that letter (case-insensitive)
 */
export function AlphabetFilter({
  selected,
  onSelect,
  counts,
  totalCount,
}: AlphabetFilterProps) {
  // The full 28-button strip is clipped at 390px viewport (audit finding L2).
  // Below the `xs` breakpoint we swap it out for a `<Select>` picker.
  const isMobile = useMediaQuery(MOBILE_MEDIA_QUERY) ?? false;

  const handleClick = (letter: AlphabetLetter) => {
    if (letter === "ALL") {
      onSelect(null);
    } else {
      // If clicking the already selected letter, deselect (go back to ALL)
      onSelect(selected === letter ? null : letter);
    }
  };

  // Get count for a letter
  const getCount = (letter: AlphabetLetter): number | undefined => {
    if (!counts) return undefined;

    if (letter === "ALL") {
      return totalCount;
    }

    if (letter === "#") {
      // Sum all numeric and special character counts
      let sum = 0;
      for (const [key, value] of counts) {
        // If key is not a-z, it's a number or special char
        if (!/^[a-z]$/.test(key)) {
          sum += value;
        }
      }
      return sum > 0 ? sum : undefined;
    }

    // Regular letter (case-insensitive)
    return counts.get(letter.toLowerCase());
  };

  const allCount = getCount("ALL");
  const hasFilter = selected !== null;

  if (isMobile) {
    const options: { value: string; label: string; disabled?: boolean }[] = [
      {
        value: "ALL",
        label:
          allCount !== undefined ? `All series (${allCount})` : "All series",
      },
      ...LETTERS.map((letter) => {
        const count = getCount(letter);
        const hasCount = count !== undefined && count > 0;
        const isEmpty = counts !== undefined && !hasCount;
        return {
          value: letter,
          label: hasCount ? `${letter} (${count})` : letter,
          disabled: isEmpty,
        };
      }),
    ];

    return (
      <Select
        aria-label="Jump to letter"
        placeholder="Jump to letter…"
        value={selected ?? "ALL"}
        onChange={(value) => {
          if (!value || value === "ALL") {
            onSelect(null);
          } else {
            onSelect(value as AlphabetLetter);
          }
        }}
        data={options}
        allowDeselect={false}
        searchable={false}
        comboboxProps={{ withinPortal: true }}
        size="sm"
      />
    );
  }

  return (
    <Group gap={4} wrap="nowrap" justify="center" className={styles.container}>
      {/* ALL button - highlighted when no filter active */}
      <Tooltip
        label={allCount ? `${allCount} series` : "All series"}
        position="bottom"
        withArrow
        openDelay={500}
      >
        <Button
          size="compact-xs"
          variant={!hasFilter ? "filled" : "subtle"}
          color={!hasFilter ? "orange" : "gray"}
          className={styles.letterButton}
          onClick={() => handleClick("ALL")}
          data-selected={!hasFilter || undefined}
        >
          ALL
        </Button>
      </Tooltip>

      {/* Letter buttons */}
      {LETTERS.map((letter) => {
        const isSelected = selected === letter;
        const count = getCount(letter);
        const hasCount = count !== undefined && count > 0;
        const isEmpty = counts !== undefined && !hasCount;

        const button = (
          <Button
            key={letter}
            size="compact-xs"
            variant={isSelected ? "filled" : "subtle"}
            color={isSelected ? "orange" : "gray"}
            className={styles.letterButton}
            onClick={() => handleClick(letter)}
            data-selected={isSelected || undefined}
            data-empty={isEmpty || undefined}
            disabled={isEmpty}
          >
            {letter}
          </Button>
        );

        // Wrap in tooltip if has count
        if (hasCount && count > 0) {
          return (
            <Tooltip
              key={letter}
              label={`${count} series`}
              position="bottom"
              withArrow
              openDelay={500}
            >
              {button}
            </Tooltip>
          );
        }

        return button;
      })}
    </Group>
  );
}
